import { useCallback, useState } from 'react';
import type { FormInstance } from 'antd';

export const ITEM_MODE_PRECISE = 'precise' as const;
export const ITEM_MODE_INPUT = 'input' as const;
export type ItemMode = typeof ITEM_MODE_PRECISE | typeof ITEM_MODE_INPUT;

const STORAGE_KEY = 'order_item_mode';

interface UseItemModeOptions {
  /**
   * When switching to `input` mode, clear `inventory_id` on every order item.
   *
   * Why: in Precise mode `inventory_id` is the BE's source of truth — it will
   * overwrite inventory_type / batch / expiration_date / custom_attributes
   * with values from the inventory record. Switching to Input mode without
   * clearing it makes user edits silently get reverted on save.
   *
   * Only edit flows need this; new-order flows have no pre-bound inventory.
   */
  clearInventoryIdOnSwitchToInput?: boolean;
}

interface UseItemModeResult {
  itemMode: ItemMode;
  setItemMode: (mode: ItemMode) => void;
  /** Persist + side-effects. Use this from <Radio.Group onChange>. */
  handleModeChange: (newMode: ItemMode) => void;
}

function readStored(): ItemMode {
  const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
  return stored === ITEM_MODE_PRECISE || stored === ITEM_MODE_INPUT ? stored : ITEM_MODE_INPUT;
}

export function useItemMode(form: FormInstance, options: UseItemModeOptions = {}): UseItemModeResult {
  const { clearInventoryIdOnSwitchToInput = false } = options;
  const [itemMode, setItemMode] = useState<ItemMode>(readStored);

  const handleModeChange = useCallback((newMode: ItemMode) => {
    setItemMode(newMode);
    window.localStorage.setItem(STORAGE_KEY, newMode);
    if (clearInventoryIdOnSwitchToInput && newMode === ITEM_MODE_INPUT) {
      const items = (form.getFieldValue('order_items') || []) as Array<Record<string, unknown>>;
      form.setFieldValue(
        'order_items',
        items.map(item => ({ ...item, inventory_id: null })),
      );
    }
  }, [form, clearInventoryIdOnSwitchToInput]);

  return { itemMode, setItemMode, handleModeChange };
}
